import React, { useState, useMemo } from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAdminUsers } from '../hooks/useAdminUsers';
import { UserFilters } from '@/types/user';

const UserList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'admin' | 'user' | ''>('');
  const [countryFilter, setCountryFilter] = useState('');

  // Build filters object
  const filters: UserFilters = useMemo(() => {
    const filterObj: UserFilters = {};
    if (searchTerm.trim()) {
      filterObj.search = searchTerm.trim();
    }
    if (roleFilter) {
      filterObj.role = roleFilter;
    }
    if (countryFilter) {
      filterObj.country = countryFilter;
    }
    return filterObj;
  }, [searchTerm, roleFilter, countryFilter]);

  const { users, isLoading, isError, error } = useAdminUsers(filters);

  // Get unique countries for filter dropdown
  const uniqueCountries = useMemo(() => {
    const countries = new Set<string>();
    users.forEach((user) => {
      if (user.country) {
        countries.add(user.country);
      }
    });
    return Array.from(countries).sort();
  }, [users]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Error loading users: {error?.message || 'Unknown error'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search by name or email"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 250 }}
        />
        <FormControl variant="outlined" sx={{ minWidth: 150 }}>
          <InputLabel>Role</InputLabel>
          <Select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as 'admin' | 'user' | '')}
            label="Role"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="user">User</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined" sx={{ minWidth: 150 }}>
          <InputLabel>Country</InputLabel>
          <Select
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            label="Country"
          >
            <MenuItem value="">All</MenuItem>
            {uniqueCountries.map((country) => (
              <MenuItem key={country} value={country}>
                {country}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Country</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user._id}>
                  <TableCell>{user._id}</TableCell>
                  <TableCell>{user.email_address}</TableCell>
                  <TableCell>
                    {user.first_name} {user.last_name}
                  </TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>{user.country || '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UserList;
